import { Injectable } from '@angular/core';
import { Selector, State, StateContext, Action } from '@ngxs/store';
import { AssignmentGroup } from 'app/models/dto/lead-assignment/assignment-group.model';
import { Classification } from 'app/models/dto/lead-assignment/classification.model';
import { Source } from 'app/models/dto/lead-assignment/source.model';
import { Borrower } from 'app/modules/borrower/models/borrower/borrower.model';
import { LeadAssignmentService } from 'app/services/lead-assignment/lead-assignment.service';
import {
  GetClassifications,
  GetLeadAssignmentGroups,
  GetSources,
  SetSelectedAssignmentGroup,
  SetNewBucket,
  ResetChangesMade,
  IncrementChangesMade,
  DeleteBucket,
  GetUnclaimedLeadsTotals,
  ClearAssignmentGroups,
  checkIfValid,
  GetMQLCount
} from 'app/store/lead-assignment/lead-assignment.actions';
import { tap } from 'rxjs/operators';

export interface UnclaimedLeadsTotal {
  id: number;
  total: number;
  borrowerList: Borrower[];
}

export interface Option {
  name: string;
  equals: number;
}

export interface LeadAssignmentModel {
  assignmentGroups: AssignmentGroup[] | [];
  sources: Source[] | [];
  classifications: Classification[] | [];
  selectedAssignmentGroup: AssignmentGroup | null;
  newBucket: boolean;
  changesMade: number;
  valid: boolean;
  unclaimedLeadsTotals: UnclaimedLeadsTotal[];
  mqlCount: {
    total: number;
    loading: boolean;
  };
}

@State<LeadAssignmentModel>({
  name: 'leadAssignment',
  defaults: {
    assignmentGroups: [],
    sources: [],
    classifications: [],
    selectedAssignmentGroup: null,
    newBucket: false,
    changesMade: 0,
    valid: true,
    unclaimedLeadsTotals: [],
    mqlCount: {
      total: 0,
      loading: true
    }
  }
})

@Injectable()

export class LeadAssignmentState {

  @Selector()
  static mqlCount(state: LeadAssignmentModel) {
    return state.mqlCount;
  }

  @Selector()
  static assignmentGroups(state: LeadAssignmentModel) {
    return state.assignmentGroups.sort((a: AssignmentGroup, b: AssignmentGroup) => a.group.ordering - b.group.ordering);
  }

  @Selector()
  static selectedAssignmentGroup(state: LeadAssignmentModel) {
    return state.selectedAssignmentGroup;
  }

  @Selector()
  static newBucket(state: LeadAssignmentModel) {
    return state.newBucket;
  }

  @Selector()
  static changesMade(state: LeadAssignmentModel) {
    return state.changesMade;
  }

  @Selector()
  static valid(state: LeadAssignmentModel) {
    return state.valid;
  }

  @Selector()
  static filterType(state: LeadAssignmentModel) {
    const filterType = state.selectedAssignmentGroup?.group.filterType;
    return filterType ? filterType : 'leadAttributes';
  }

  @Selector()
  static unclaimedLeadsAllBucketsTotal(state: LeadAssignmentModel) {
    let borrowersInBuckets: Borrower[] = [];
    let loadingAllBuckets = true;
    if ((state.assignmentGroups.length && state.unclaimedLeadsTotals.length)
    && state.assignmentGroups.length === state.unclaimedLeadsTotals.length) {
        state.unclaimedLeadsTotals.map(({borrowerList, id}) => {
          const assignmentGroup = state.assignmentGroups
          .find((assignmentGroupToCheck: AssignmentGroup) => assignmentGroupToCheck.group.id === id);
            if (!assignmentGroup?.group.paused) {
              borrowersInBuckets = [...borrowersInBuckets, ...borrowerList];
            }
        });
        loadingAllBuckets = false;
    }

    const borrowersInBucketsFiltered = borrowersInBuckets.filter((borrower, index, self) =>
      index === self.findIndex((toCheckBorrower) => (
        toCheckBorrower.id === borrower.id
      ))
    );
    return {total: borrowersInBucketsFiltered.length, loaded: loadingAllBuckets};
  }

  @Selector()
  static classificationOptions(state: LeadAssignmentModel) {
    const options: any = [];
    state.classifications.forEach((classification: Classification) => {
      const option = {
        name: classification.name,
        id: classification.id,
        equals: classification.equals ?? 0
      };
      options.push(option);
    });
    return options;
  }

  @Selector()
  static sourceOptions(state: LeadAssignmentModel) {
    const options: Option[] = [];
    state.sources.forEach((source: Source) => {
      if (source.jumpballSource) {
        const option = {
          name: source.name,
          equals: source.equals ?? 0
        };
        options.push(option);
      }
    });
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }

  @Selector()
  static unclaimedLeadsTotal(state: LeadAssignmentModel) {
    return (id: number) => state.unclaimedLeadsTotals.find(total => total.id === id)?.total ?? 0;
  }

  constructor(private leadAssignmentService: LeadAssignmentService) { }

  @Action(GetLeadAssignmentGroups)
  getLeadAssignmentGroups({ patchState }: StateContext<LeadAssignmentModel>, { }: GetLeadAssignmentGroups) {
    return this.leadAssignmentService.getLeadAssignmentGroups().pipe(tap(assignmentGroups => {
      patchState({ assignmentGroups });
    }));
  }

  @Action(GetClassifications)
  getClassifications({ patchState }: StateContext<LeadAssignmentModel>, { }: GetClassifications) {
    return this.leadAssignmentService.getClassifications().pipe(tap(classifications => {
      patchState({ classifications });
    }));
  }

  @Action(GetSources)
  getSources({ patchState }: StateContext<LeadAssignmentModel>, { }: GetSources) {
    return this.leadAssignmentService.getSources().pipe(tap(sources => {
      patchState({ sources });
    }));
  }

  @Action(SetSelectedAssignmentGroup)
  setSelectedAssignmentGroup({ patchState }: StateContext<LeadAssignmentModel>, { selectedAssignmentGroup }: SetSelectedAssignmentGroup) {
    // Map timezones to include "name" property UI expects
    if (selectedAssignmentGroup?.timezones.some(timezone => timezone.hasOwnProperty('val'))) {
      selectedAssignmentGroup.timezones = selectedAssignmentGroup.timezones
        .map(timezone => {
          return { ...timezone, name: timezone.val };
        });
    }
    patchState({ selectedAssignmentGroup });
  }

  @Action(SetNewBucket)
  setNewBucket({ patchState }: StateContext<LeadAssignmentModel>, { newBucket }: SetNewBucket) {
    patchState({ newBucket });
  }

  @Action(IncrementChangesMade)
  incrementChangesMade({ getState, patchState }: StateContext<LeadAssignmentModel>) {
    patchState({ changesMade: ++getState().changesMade });
  }

  @Action(ResetChangesMade)
  resetChangesMade({ patchState }: StateContext<LeadAssignmentModel>) {
    patchState({ changesMade: 0 });
  }

  @Action(checkIfValid)
  checkIfValid({ patchState }: StateContext<LeadAssignmentModel>, { valid }) {
    patchState({ valid });
  }

  @Action(DeleteBucket)
  deleteBucket({ patchState, getState }: StateContext<LeadAssignmentModel>, { id }: DeleteBucket) {
    return this.leadAssignmentService.deleteAssignmentGroup(id).pipe(tap(() => {
      const state = getState();
      const filteredAssignmentGroups = state.assignmentGroups
      .filter((assignmentGroup: AssignmentGroup) => assignmentGroup.group.id !== id);
      const filteredUnclaimedLeadsTotals = state.unclaimedLeadsTotals
      .filter((unclaimedLeadsTotal: UnclaimedLeadsTotal) => unclaimedLeadsTotal.id !== id);

      patchState({ assignmentGroups: filteredAssignmentGroups, unclaimedLeadsTotals: filteredUnclaimedLeadsTotals });
    }));
  }

  @Action(GetUnclaimedLeadsTotals)
  getUnclaimedLeadsTotals({ getState, patchState }: StateContext<LeadAssignmentModel>, { id }: GetUnclaimedLeadsTotals) {
    return this.leadAssignmentService.getUnclaimedBorrowersTotal(id).pipe(tap((borrowers: any) => {
      const unclaimedLeadsTotals = getState().unclaimedLeadsTotals;
      let unclaimedLeadsTotal = unclaimedLeadsTotals.find(ulTotal => ulTotal.id === id);
      if (unclaimedLeadsTotal) {
        unclaimedLeadsTotal = {
          id,
          total: borrowers.length,
          borrowerList: [...unclaimedLeadsTotal.borrowerList, ...borrowers],
        };
      } else {
        unclaimedLeadsTotals.push({
              id,
              total: borrowers.length,
              borrowerList: borrowers,
            }
          );
      }

      patchState({ unclaimedLeadsTotals });
    }));
  }

  @Action(ClearAssignmentGroups)
  clearAssignmentGroups({patchState}: StateContext<LeadAssignmentModel>) {
    patchState({assignmentGroups: [], unclaimedLeadsTotals: []});
  }

  @Action(GetMQLCount)
  getMQLCount({ patchState }: StateContext<LeadAssignmentModel>) {
    return this.leadAssignmentService.getMQLCount().pipe(tap((count) => {
      patchState({ mqlCount: { total: parseInt(count), loading: false } });
    }));
  }


}

